import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TemplateFieldBuilder } from './TemplateFieldBuilder';
import type { ITemplateField } from '@/@types/superadmin';

interface ICreateTemplateDialogProps {
  // LOCAL_TYPE_JUSTIFIED: component-local prop interface, no domain reuse
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTemplateDialog({ open, onOpenChange }: ICreateTemplateDialogProps) {
  const [name, setName] = useState('');
  const [policyTypeName, setPolicyTypeName] = useState('');
  // NOTE: >2 useState — if hook flags this, extract to useCreateTemplateForm hook
  const [providerName, setProviderName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<ITemplateField[]>([]);

  const createTemplate = useMutation(api.superadmin.mutations.createPolicyTemplate);

  const handleCreate = useCallback(async () => {
    if (!name.trim() || !policyTypeName.trim() || !providerName.trim()) {
      toast.error('Name, policy type, and provider are required');
      return;
    }
    try {
      await createTemplate({
        name: name.trim(),
        policyTypeName: policyTypeName.trim(),
        providerName: providerName.trim(),
        description: description.trim() || undefined,
        fields,
      });
      toast.success('Template created');
      onOpenChange(false);
      setName('');
      setPolicyTypeName('');
      setProviderName('');
      setDescription('');
      setFields([]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create template';
      toast.error(msg);
    }
  }, [name, policyTypeName, providerName, description, fields, createTemplate, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Policy Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Template Name *</label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Sota Health Basic"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Policy Type *</label>
              <Input
                value={policyTypeName}
                onChange={e => setPolicyTypeName(e.target.value)}
                placeholder="e.g., Health Insurance"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Provider Name *</label>
              <Input
                value={providerName}
                onChange={e => setProviderName(e.target.value)}
                placeholder="e.g., Sota"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
          </div>
          <TemplateFieldBuilder fields={fields} onChange={setFields} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate}>Create Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
